import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  FormHelperText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Comment as CommentIcon,
  Cancel as CancelIcon,
  Contacts as ContactsIcon
} from '@mui/icons-material';
// Using standard HTML date inputs instead of MUI DatePicker for compatibility
import { opportunityService } from '../services/opportunityService';
import { contactService } from '../services/contactService';
import { companyService } from '../services/companyService';
import { lobService } from '../services/lobService';
import salesPersonService from '../services/salesPersonService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import userService from '../services/userService';
import OpportunityMapSection from '../components/OpportunityMapSection';
import { DEAL_STAGES, CLOSED_STAGES } from '../constants/dealStages';
import UserDisplay from '../components/UserDisplay';
import ContactSelector from '../components/ContactSelector';
import useAdminStatus from '../hooks/useAdminStatus';
import { emailService } from '../services/emailService';

const Opportunity = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const currentUser = useCurrentUser();
  const { isAdmin } = useAdminStatus();

  // State management
  const [opportunity, setOpportunity] = useState(null);
  const [originalOpportunity, setOriginalOpportunity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isTestOpportunity, setIsTestOpportunity] = useState(false);

  // Initialize opportunity when currentUser is available
  useEffect(() => {
    if (!isEditing && currentUser.email && !opportunity) {
      const newOpportunity = opportunityService.createEmptyOpportunity(currentUser.email);
      newOpportunity.salesPersonEmails = [currentUser.email];
      setOpportunity(newOpportunity);
      setOriginalOpportunity(JSON.parse(JSON.stringify(newOpportunity)));
    }
  }, [isEditing, currentUser.email, opportunity]);

  // Ensure current user is always in salesPersonEmails for new opportunities
  useEffect(() => {
    if (!isEditing && opportunity && currentUser.email) {
      if (!opportunity.salesPersonEmails || !opportunity.salesPersonEmails.includes(currentUser.email)) {
        setOpportunity(prev => ({
          ...prev,
          salesPersonEmails: [currentUser.email]
        }));
      }
    }
  }, [isEditing, opportunity, currentUser.email]);

  // Data for dropdowns
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [lobOptions, setLobOptions] = useState([]);
  
  // Centralized user display data to avoid repeated API calls
  const [userDisplayData, setUserDisplayData] = useState({});

  // UI state
  const [newComment, setNewComment] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeDialogData, setCloseDialogData] = useState({
    stage: '',
    value: '',
    date: new Date(),
    reason: ''
  });

  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Load user display data for all users in the opportunity
  const loadUserDisplayData = useCallback(async (opportunity) => {
    const emails = new Set();
    
    // Always include current user
    if (currentUser.email) emails.add(currentUser.email);
    
    // Collect all emails from the opportunity
    if (opportunity.createdByEmail) emails.add(opportunity.createdByEmail);
    if (opportunity.salesPersonEmails) {
      opportunity.salesPersonEmails.forEach(email => emails.add(email));
    }
    if (opportunity.moments) {
      opportunity.moments.forEach(moment => {
        if (moment.createdByEmail) emails.add(moment.createdByEmail);
      });
    }
    if (opportunity.comments) {
      opportunity.comments.forEach(comment => {
        if (comment.createdByEmail) emails.add(comment.createdByEmail);
      });
    }
    
    // Load user data for all emails
    const emailArray = Array.from(emails);
    await userService.preloadUsers(emailArray);
    
    // Build user display data object
    const displayData = {};
    for (const email of emailArray) {
      try {
        // Get user info from Graph API
        const userInfo = await userService.getUserByEmail(email);
        if (userInfo) {
          displayData[email] = userInfo;

          // Ensure user exists in Salespersons table
          const existingSalespersons = salesPersons.map(sp => sp.Email || sp.email);
          if (!existingSalespersons.includes(email)) {
            try {
              await salesPersonService.createSalesperson(
                email,
                userInfo.name || email.split('@')[0],
                userInfo.name ? userInfo.name.split(' ').map(n => n[0]).join('') : email[0].toUpperCase()
              );
            } catch (error) {
              // Continue anyway since the user might already exist
            }
          }
        }
      } catch (error) {
        // Create fallback user data
        displayData[email] = {
          email: email,
          name: email.split('@')[0],
          photoUrl: null,
          initials: email.charAt(0).toUpperCase()
        };
      }
    }
    
    setUserDisplayData(displayData);
  }, [currentUser.email, salesPersons]);

  // Load contact details
  const loadContactDetails = useCallback(async (contactIds) => {
    try {
      const contactPromises = contactIds.map(contactId => contactService.getContact(contactId));
      const contactsData = await Promise.all(contactPromises);
      
      // Transform contact data to ensure consistent casing
      const transformedContacts = contactsData.map(contact => ({
        contactId: contact.Id || contact.id,
        firstName: contact.FirstName || contact.firstName,
        lastName: contact.LastName || contact.lastName,
        email: contact.Email || contact.email,
        title: contact.Title || contact.title,
        sortOrder: contact.SortOrder || contact.sortOrder
      }));
      
      return transformedContacts;
    } catch (err) {
      setError(prev => prev + '\nFailed to load contact details: ' + err.message);
      return [];
    }
  }, []);

  // Load contacts for a company
  const loadContacts = async (companyId) => {
    try {
      if (!companyId) {
        const unassignedContacts = await contactService.getContactsByCompany(null);
        setContacts(unassignedContacts);
        return;
      }
      
      const companyContacts = await contactService.getContactsByCompany(companyId);
      setContacts(companyContacts);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(prev => prev + '\nFailed to load contacts: ' + err.message);
      setContacts([]);
    }
  };

  // Load data
  const loadData = useCallback(async (forceRefresh = false) => {
    if (loading && !forceRefresh) return; // Prevent concurrent loads unless forced
    
    // For new opportunities, wait for currentUser.email to be available
    if (!isEditing && !currentUser.email) {
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Load all reference data first
      const [companiesData, lobData, salesPersonsData] = await Promise.all([
        companyService.getCompanies().catch(err => {
          setError(prev => prev + '\nFailed to load companies: ' + err.message);
          return [];
        }),
        lobService.getLOBOptions().catch(err => {
          setError(prev => prev + '\nFailed to load LOB options: ' + err.message);
          return [];
        }),
        salesPersonService.getSalesPersons().catch(err => {
          setError(prev => prev + '\nFailed to load salespersons: ' + err.message);
          return [];
        })
      ]);

      setCompanies(companiesData);
      setLobOptions(lobData);
      setSalesPersons(salesPersonsData);

      // Now load the opportunity if editing
      if (isEditing) {
        const opportunityData = await opportunityService.getOpportunity(id);
        if (opportunityData) {
          const converted = await convertOpportunityFormat(opportunityData);
          setOpportunity(converted);
          setOriginalOpportunity(converted);
          
          // Load contacts for the opportunity's company
          if (converted.companyId) {
            await loadContacts(converted.companyId);
          }
          
          // Load user display data
          await loadUserDisplayData(converted);
        } else {
          setError(prev => prev + '\nOpportunity not found');
        }
      } else {
        // For new opportunities, create with current user as salesperson
        console.log('Creating new opportunity with current user:', currentUser);

        // Ensure current user exists in Salespersons table
        const existingSalespersons = salesPersonsData.map(sp => sp.Email || sp.email);
        if (!existingSalespersons.includes(currentUser.email)) {
          try {
            // Create salesperson entry for current user
            await salesPersonService.createSalesperson(
              currentUser.email,
              currentUser.name || currentUser.email.split('@')[0],
              currentUser.name ? currentUser.name.split(' ').map(n => n[0]).join('') : currentUser.email[0].toUpperCase()
            );
            console.log('Created salesperson entry for current user');
          } catch (error) {
            console.error('Failed to create salesperson entry:', error);
            // Continue anyway since the user might already exist
          }
        }

        const newOpportunity = opportunityService.createEmptyOpportunity(currentUser.email);
        console.log('New opportunity created:', newOpportunity);
        
        // Ensure the current user is set as a salesperson
        if (!newOpportunity.salesPersonEmails.includes(currentUser.email)) {
          newOpportunity.salesPersonEmails = [currentUser.email];
        }
        
        // Set the opportunity in state
        setOpportunity(newOpportunity);
        
        // Load unassigned contacts for new opportunity
        const unassignedContacts = await contactService.getContactsByCompany(null);
        setContacts(unassignedContacts);
        
        // Load user display data for current user
        await loadUserDisplayData(newOpportunity);
      }
      
      if (forceRefresh) {
        setSuccess('Data refreshed successfully');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEditing, currentUser.email, currentUser.name, loadUserDisplayData, loadContactDetails]);

  // Initial data load - only when currentUser.email is available (for new opportunities)
  useEffect(() => {
    if (isEditing || currentUser.email) {
      loadData();
    }
  }, [loadData, isEditing, currentUser.email]);

  // Load contacts when company changes
  useEffect(() => {
    if (loading || !opportunity) return;
    
    const loadContacts = async () => {
      try {
        // Load both company contacts and specific contacts for this opportunity
        const [companyContacts, opportunityContacts] = await Promise.all([
          contactService.getContactsByCompany(opportunity.companyId || null, opportunity.opportunityId),
          Promise.all((opportunity.contactIds || []).map(id => contactService.getContact(id)))
        ]);
        
        // Combine and deduplicate contacts
        const allContacts = [...companyContacts];
        opportunityContacts.forEach(contact => {
          if (contact && !allContacts.some(c => (c.contactId || c.ContactId) === (contact.contactId || contact.ContactId))) {
            allContacts.push(contact);
          }
        });
        
        setContacts(allContacts);
      } catch (err) {
        setError(prev => prev + '\nFailed to load contacts: ' + err.message);
      }
    };

    // Only load contacts if we're not in the initial data load
    if (!loading) {
      loadContacts();
    }
  }, [opportunity?.companyId, opportunity?.contactIds, opportunity?.opportunityId]); // Depend on both companyId and contactIds changes

  // Monitor opportunity state changes - but don't trigger API calls
  useEffect(() => {
    // Only log changes, don't trigger any data loading
  }, [opportunity]);

  // Note: Auto-refresh removed from individual entity pages to prevent data loss
  // Manual refresh is still available via the refresh button

  // Convert opportunity from old format to new format
  const convertOpportunityFormat = async (opportunity) => {
    // Map all fields from the input opportunity to the correct format
    const converted = {
      opportunityId: opportunity.Id || opportunity.opportunityId || opportunity.id || '',
      opportunityName: opportunity.OpportunityName || opportunity.opportunityName || '',
      dealStage: opportunity.DealStage || opportunity.dealStage || '',
      // Handle company data more carefully
      companyId: opportunity.CompanyId || opportunity.companyId || '',
      companyName: opportunity.CompanyName || opportunity.companyName || '',
      lineOfBusinessId: opportunity.LineOfBusinessId || opportunity.lineOfBusinessId,
      lineOfBusinessName: opportunity.LineOfBusinessName || opportunity.lineOfBusinessName || '',
      estimatedValue: opportunity.EstimatedValue || opportunity.estimatedValue,
      estimatedCloseDate: opportunity.EstimatedCloseDate || opportunity.estimatedCloseDate,
      probability: opportunity.Probability || opportunity.probability,
      nextStep: opportunity.NextStep || opportunity.nextStep || '',
      competitorAnalysis: opportunity.CompetitorAnalysis || opportunity.competitorAnalysis || '',
      closedValue: opportunity.ClosedValue || opportunity.closedValue,
      closedDate: opportunity.ClosedDate || opportunity.closedDate,
      cancelledReason: opportunity.CancelledReason || opportunity.cancelledReason || '',
      createdByEmail: opportunity.CreatedByEmail || opportunity.createdByEmail || '',
      createdDate: opportunity.CreatedDate || opportunity.createdDate || '',
      modifiedByEmail: opportunity.ModifiedByEmail || opportunity.modifiedByEmail || '',
      modifiedDate: opportunity.ModifiedDate || opportunity.modifiedDate || '',
      description: opportunity.Description || opportunity.description || '',
      // Ensure these arrays are always initialized
      contacts: opportunity.contacts || [],
      contactIds: opportunity.contacts ? opportunity.contacts.map(c => c.contactId || c.ContactId) : (opportunity.contactIds || []),
      salesPersonEmails: opportunity.salesPersonEmails || [],
      comments: opportunity.comments || [],
      moments: opportunity.moments || []
    };



    // Ensure IDs are lowercase for consistency
    if (converted.opportunityId) {
      converted.opportunityId = converted.opportunityId.toLowerCase();
    }
    if (converted.companyId) {
      converted.companyId = converted.companyId.toLowerCase();
    }
    
    return converted;
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setOpportunity(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle estimated value blur (only create moment when user finishes editing)
  const handleEstimatedValueBlur = (value) => {
    if (!isEditing) return;
    const oldValNum = estFocusRef.current == null || estFocusRef.current === '' ? null : Number(estFocusRef.current);
    const newValNum = value == null || value === '' ? null : Number(value);
    if (oldValNum === newValNum) return;
    const formattedOldValue = formatCurrencyForDisplay(oldValNum || 0);
    const formattedNewValue = formatCurrencyForDisplay(newValNum || 0);
      setOpportunity(prev => {
      const updated = { ...prev, estimatedValue: newValNum };
        const tempMoment = {
        id:`temp_${uuidv4()}`,
        message:`Estimated value is being changed from ${formattedOldValue} to ${formattedNewValue}.`,
          createdByEmail: currentUser.email,
          createdDate: new Date().toISOString(),
        createdByName: currentUser.name || currentUser.email
      };
      updated.moments=[...(updated.moments||[]),tempMoment];
      updated.pendingMoment={message:tempMoment.message,createdByEmail:currentUser.email,tempId:tempMoment.id};
      return updated;
    });
  };

  const probFocusRef = useRef(null);
  const estFocusRef = useRef(null);
  const estCloseDateRef = useRef(null);

  // Handle probability blur (only create moment when user finishes editing)
  const handleProbabilityBlur = () => {
    if (!isEditing) return;
    const oldVal = probFocusRef.current;
    const newVal = opportunity.probability;
    
    // Compare values properly - handle null, undefined, and NaN cases
    const oldValForComparison = (oldVal == null || isNaN(oldVal)) ? null : Number(oldVal);
    const newValForComparison = (newVal == null || isNaN(newVal)) ? null : Number(newVal);
    
    if (oldValForComparison === newValForComparison) return;
    
    setOpportunity(prev => {
      const updated = { ...prev };
      const tempMoment = {
        id: `temp_${uuidv4()}`,
        message: `Probability changed from ${oldValForComparison || 0}% to ${newValForComparison || 0}%.`,
        createdByEmail: currentUser.email,
        createdDate: new Date().toISOString(),
        createdByName: currentUser.name || currentUser.email
      };
      updated.moments = [...(updated.moments || []), tempMoment];
      updated.pendingMoment = { message: tempMoment.message, createdByEmail: currentUser.email, tempId: tempMoment.id };
      return updated;
    });
  };

  // Convert a date string to UTC while preserving the date in US Eastern timezone
  const convertToUTCPreservingET = (dateStr) => {
    if (!dateStr) return null;
    
    // Parse the input date string (which is in YYYY-MM-DD format)
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Create a date object in ET timezone at midnight
    const etDate = new Date();
    etDate.setFullYear(year);
    etDate.setMonth(month - 1); // Month is 0-based
    etDate.setDate(day);
    etDate.setHours(0, 0, 0, 0);
    
    // Convert to ISO string and extract just the date part
    return etDate.toISOString().split('T')[0] + 'T00:00:00.000Z';
  };

  // Format a date string for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    // Format the date in US Eastern timezone
    return new Date(dateStr).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  // Handle estimated close date blur (only create moment when user finishes editing)
  const handleEstimatedCloseDateBlur = (value) => {
    if (!isEditing) return;
    const oldDate = estCloseDateRef.current;
    const newDate = value;
    
    // Skip if both dates are empty or equal
    if ((oldDate === null && newDate === '') || 
        (oldDate === '' && newDate === null) || 
        oldDate === newDate) return;

    const formatDate = (dateStr) => {
      if (!dateStr) return 'none';
      // Format date in US Eastern timezone
      const [datePart] = dateStr.split('T');
      const date = new Date(datePart + 'T12:00:00'); // Use noon to avoid any timezone issues
      return date.toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });
    };

    setOpportunity(prev => {
      const updated = { ...prev, estimatedCloseDate: newDate };
      const tempMoment = {
        id: `temp_${uuidv4()}`,
        message: `Estimated close date is being changed from ${formatDate(oldDate)} to ${formatDate(newDate)}.`,
        createdByEmail: currentUser.email,
        createdDate: new Date().toISOString(),
        createdByName: currentUser.name || currentUser.email
      };
      updated.moments = [...(updated.moments || []), tempMoment];
      updated.pendingMoment = {
        message: tempMoment.message,
        createdByEmail: currentUser.email,
        tempId: tempMoment.id
      };
      return updated;
    });
  };

  // Handle sales person changes
  const handleAddSalesPerson = async (person) => {
    const email = person.Email;
    if (!opportunity.salesPersonEmails.includes(email)) {
      try {
        // Ensure salesperson exists in Salespersons table
        const existingSalespersons = salesPersons.map(sp => sp.Email || sp.email);
        if (!existingSalespersons.includes(email)) {
          await salesPersonService.createSalesperson(
            email,
            person.Name,
            person.Name.split(' ').map(n => n[0]).join('')
          );
          console.log('Created salesperson entry for:', email);
        }

        // Update local state only
        const updatedSalesPersonEmails = [...opportunity.salesPersonEmails, email];
        
        setOpportunity(prev => {
          // Add the salesperson
          const updated = { ...prev, salesPersonEmails: updatedSalesPersonEmails };
          
          // Add a moment for the change
          const newMoment = {
            id: uuidv4(),
            message: `Sales person ${person.Name} added.`,
            createdByEmail: currentUser.email,
            createdDate: new Date().toISOString()
          };
          updated.moments = [...(updated.moments || []), newMoment];
          
          return updated;
        });
      } catch (error) {
        console.error('Error adding salesperson:', error);
        setError('Failed to add salesperson: ' + error.message);
      }
    }
  };

  // State for salesperson operations
  const [removingSalesperson, setRemovingSalesperson] = useState(null);

  const handleRemoveSalesPerson = async (email) => {
    try {
      setRemovingSalesperson(email);

      // Get user info from centralized data
      let userName = email;
      const userData = userDisplayData[email];
      if (userData?.name) {
        userName = userData.name;
      }

      // Update local state
      const updatedSalesPersonEmails = opportunity.salesPersonEmails.filter(e => e !== email);
      setOpportunity(prev => {
        // Remove the salesperson
        const updated = { ...prev, salesPersonEmails: updatedSalesPersonEmails };
        
        // Add a moment for the change
        const newMoment = {
          id: uuidv4(),
          message: `Sales person ${userName} removed.`,
          createdByEmail: currentUser.email,
          createdDate: new Date().toISOString(),
          createdByName: currentUser.name || currentUser.email
        };
        updated.moments = [...(updated.moments || []), newMoment];
        
        return updated;
      });
    } catch (error) {
      console.error('Error removing salesperson:', error);
      setError('Failed to remove salesperson: ' + error.message);
    } finally {
      setRemovingSalesperson(null);
    }
  };

  const handleMoveSalesPerson = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < opportunity.salesPersonEmails.length) {
      const updatedSalesPersonEmails = [...opportunity.salesPersonEmails];
      [updatedSalesPersonEmails[index], updatedSalesPersonEmails[newIndex]] = 
      [updatedSalesPersonEmails[newIndex], updatedSalesPersonEmails[index]];
      
      // Update local state
      setOpportunity(prev => ({ ...prev, salesPersonEmails: updatedSalesPersonEmails }));

      // If this is an existing opportunity, update the sort order in the backend
      if (isEditing) {
        const email = opportunity.salesPersonEmails[index];
        const newEmail = opportunity.salesPersonEmails[newIndex];
        Promise.all([
          opportunityService.updateSalespersonOrder(opportunity.opportunityId, email, newIndex + 1),
          opportunityService.updateSalespersonOrder(opportunity.opportunityId, newEmail, index + 1)
        ]).catch(err => {
          console.error('Error updating salesperson order:', err);
          setError('Failed to update salesperson order');
        });
      }
    }
  };

  // Handle contact changes
  const handleAddContact = async (contact) => {
    const contactId = (contact.contactId || '').toLowerCase();
    const currentContactIds = opportunity.contactIds || [];
    
    // Skip if contact is already in the list (case-insensitive comparison)
    if (!currentContactIds.some(id => (id || '').toLowerCase() === contactId)) {
      const updatedContactIds = [...currentContactIds, contactId];
      
      // Get full contact details
      const contactDetails = await contactService.getContact(contactId);
      const currentContacts = opportunity.contacts || [];
      const updatedContacts = [...currentContacts, contactDetails];
      
      setOpportunity(prev => ({ 
        ...prev, 
        contactIds: updatedContactIds,
        contacts: updatedContacts
      }));
    }
  };

  const handleRemoveContact = (contactId) => {
    const updatedContactIds = opportunity.contactIds.filter(id => id !== contactId);
    setOpportunity(prev => ({ ...prev, contactIds: updatedContactIds }));
  };

  const handleMoveContact = (index, direction) => {
    const contactIds = opportunity.contactIds || [];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < contactIds.length) {
      const updatedContactIds = [...contactIds];
      [updatedContactIds[index], updatedContactIds[newIndex]] = 
      [updatedContactIds[newIndex], updatedContactIds[index]];
      
      // Update local state
      setOpportunity(prev => ({ ...prev, contactIds: updatedContactIds }));

      // If this is an existing opportunity, update the sort order in the backend
      if (isEditing) {
        const contactId = contactIds[index];
        const newContactId = contactIds[newIndex];
        Promise.all([
          opportunityService.updateContactOrder(opportunity.opportunityId, contactId, newIndex + 1),
          opportunityService.updateContactOrder(opportunity.opportunityId, newContactId, index + 1)
        ]).catch(err => {
          console.error('Error updating contact order:', err);
          setError('Failed to update contact order');
        });
      }
    }
  };

  // Handle comments
  const handleAddComment = async () => {
    if (newComment.trim()) {
      const comment = {
        id: uuidv4(),
        text: newComment.trim(),
        createdByEmail: currentUser.email,
        createdDate: new Date().toISOString()
      };
      
      // Auto-save the comment to backend immediately (only for existing opportunities)
      if (isEditing) {
        setSavingComment(true);
        try {
          // Save comment to backend first
          await opportunityService.createComment(opportunity.opportunityId, comment.text, currentUser.email);
          
          // Update local state after successful save
      const updatedComments = [...opportunity.comments, comment];
      const updatedOpportunity = { ...opportunity, comments: updatedComments };
      setOpportunity(updatedOpportunity);
          setOriginalOpportunity(JSON.parse(JSON.stringify(updatedOpportunity)));
      
      // Update user display data
      const userInfo = await userService.getUserByEmail(currentUser.email);
      if (userInfo) {
        setUserDisplayData(prev => ({ ...prev, [currentUser.email]: userInfo }));
      }
      
      setNewComment('');
          setSuccess('Comment added successfully');
        } catch (err) {
          console.error('Error saving comment:', err);
          setError('Failed to save comment');
        } finally {
          setSavingComment(false);
        }
      } else {
        // For new opportunities, just update local state
        const updatedComments = [...opportunity.comments, comment];
        const updatedOpportunity = { ...opportunity, comments: updatedComments };
        setOpportunity(updatedOpportunity);
        
        // Update user display data
        const userInfo = await userService.getUserByEmail(currentUser.email);
        if (userInfo) {
          setUserDisplayData(prev => ({ ...prev, [currentUser.email]: userInfo }));
        }
        
        setNewComment('');
        setSuccess('Comment added. It will be saved when you create the opportunity.');
      }
    }
  };

  // Handle comment editing
  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text);
  };

  // Handle comment save
  const handleSaveComment = async () => {
    if (!editingCommentText.trim()) {
      setError('Comment text cannot be empty');
      return;
    }

    setSavingComment(true);
    try {
      // Update comment in backend
      await opportunityService.updateComment(
        opportunity.opportunityId,
        editingCommentId,
        editingCommentText,
        currentUser.email
      );
      
      // Update comment in local state
      const updatedComments = opportunity.comments.map(comment => 
        comment.id === editingCommentId 
          ? {
              ...comment,
              text: editingCommentText,
              modifiedByEmail: currentUser.email,
              modifiedDate: new Date().toISOString()
            }
          : comment
      );
      
      setOpportunity(prev => ({
        ...prev,
        comments: updatedComments
      }));
      
      setEditingCommentId(null);
      setEditingCommentText('');
      setSuccess('Comment updated successfully');
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Failed to update comment: ' + err.message);
    } finally {
      setSavingComment(false);
    }
  };

  // Handle comment cancel edit
  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setDeletingCommentId(commentId);
    try {
      // Delete comment from backend
      await opportunityService.deleteComment(opportunity.opportunityId, commentId);
      
      // Remove comment from local state
      const updatedComments = opportunity.comments.filter(comment => comment.id !== commentId);
      const updatedOpportunity = { ...opportunity, comments: updatedComments };
      setOpportunity(updatedOpportunity);
      setOriginalOpportunity(JSON.parse(JSON.stringify(updatedOpportunity)));
      
      setSuccess('Comment deleted successfully');
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDeleteOpportunity = async () => {
    if (!window.confirm('Are you sure you want to delete this opportunity? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      
      // Check if opportunity can be deleted
      const deletionCheck = await opportunityService.checkOpportunityDeletion(opportunity.opportunityId);
      
      if (!deletionCheck.canDelete) {
        // Show error with talent links
        const talentLinks = deletionCheck.talentDetails.map(talent => 
          `${talent.name} (${talent.id})`
        ).join(', ');
        
        setError(`Cannot delete opportunity. It has ${deletionCheck.jobHistoryCount} job history record(s) associated with the following talents: ${talentLinks}`);
        return;
      }
      
      // Proceed with deletion
      await opportunityService.deleteOpportunity(opportunity.opportunityId);
      setSuccess('Opportunity deleted successfully');
      navigate('/opportunities');
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      setError(error.message || 'Failed to delete opportunity');
    } finally {
      setSaving(false);
    }
  };

  // Handle stage change to closed stages
  const handleStageChange = (newStage) => {
    const isMovingToClosed = CLOSED_STAGES.includes(newStage) && 
                            !CLOSED_STAGES.includes(opportunity.dealStage);
    
    if (isMovingToClosed) {
      // Set initial value based on the selected stage
      let initialValue = opportunity.estimatedValue || 0;
      if (newStage === DEAL_STAGES.CLOSED_LOST) {
        initialValue = 0;
      }
      
      setCloseDialogData({
        stage: newStage,
        value: initialValue,
        date: new Date(),
        reason: ''
      });
      setCloseDialogOpen(true);
    } else {
      // For non-closed stages, update directly
      const oldStage = opportunity.dealStage;
      setOpportunity(prev => {
        const updated = { ...prev, dealStage: newStage };

        // Only create moments for existing opportunities (when editing)
        if (isEditing) {
          // Create a temporary moment for UI display
          const tempMoment = {
            id: `temp_${uuidv4()}`,
            message: `Deal stage changed from ${oldStage || 'none'} to ${newStage || 'none'}.`,
            createdByEmail: currentUser.email,
            createdDate: new Date().toISOString(),
            createdByName: currentUser.name || currentUser.email
          };

          // Add the moment to the UI
          updated.moments = [...(updated.moments || []), tempMoment];

          // Store the moment to be created after saving
          updated.pendingMoment = {
            message: tempMoment.message,
            createdByEmail: currentUser.email,
            tempId: tempMoment.id
          };
        }

        return updated;
      });
    }
  };

  // Handle close dialog
  const handleCloseDialogSave = () => {
    // Format the date properly for SQL Server
    const formatDateForSQL = (date) => {
      if (!date) return null;
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().split('T')[0];
    };

    const oldStage = opportunity.dealStage;
    const formattedDate = CLOSED_STAGES.includes(closeDialogData.stage) ? 
      formatDateForSQL(closeDialogData.date) : 
      null;

    // Create the moment message
    let momentMessage = `Deal stage changed from ${oldStage || 'none'} to ${closeDialogData.stage || 'none'}`;
      if (closeDialogData.stage === DEAL_STAGES.CLOSED_WON && closeDialogData.value) {
        const formattedValue = formatCurrencyForDisplay(closeDialogData.value);
        momentMessage += ` with closed value ${formattedValue || '$0'}`;
      }
      momentMessage += '.';
      
    setOpportunity(prev => {
      const updated = {
        ...prev,
        dealStage: closeDialogData.stage,
        closedDate: formattedDate,
        closedValue: CLOSED_STAGES.includes(closeDialogData.stage) ? closeDialogData.value : null,
        cancelledReason: closeDialogData.stage === DEAL_STAGES.CANCELLED ? closeDialogData.reason : ''
      };

      // Only create moments for existing opportunities (when editing)
      if (isEditing) {
        // Create a temporary moment for UI display
        const tempMoment = {
          id: `temp_${uuidv4()}`,
          message: momentMessage,
          createdByEmail: currentUser.email,
          createdDate: new Date().toISOString(),
          createdByName: currentUser.name || currentUser.email
        };

        // Add the moment to the UI
        updated.moments = [...(prev.moments || []), tempMoment];

        // Store the moment to be created after saving
        updated.pendingMoment = {
          message: momentMessage,
          createdByEmail: currentUser.email,
          tempId: tempMoment.id
        };
      }

      return updated;
    });

    setCloseDialogOpen(false);
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate required fields
      const validationErrors = {};
      
      if (!opportunity.opportunityName || opportunity.opportunityName.trim() === '') {
        validationErrors.opportunityName = 'Opportunity Name is required';
      }
      
      if (!opportunity.lineOfBusinessId) {
        validationErrors.lineOfBusinessId = 'Line of Business is required';
      }

      if (Object.keys(validationErrors).length > 0) {
        setValidationErrors(validationErrors);
        
        // Create a user-friendly toast message
        const errorMessages = Object.values(validationErrors);
        const toastMessage = errorMessages.length === 1 
          ? errorMessages[0]
          : `Please fix the following errors:\n• ${errorMessages.join('\n• ')}`;
        
        setError(toastMessage);
        setSaving(false);
        return;
      }

      // Clear any existing validation errors if validation passes
      setValidationErrors({});

      // Ensure current user is in salesPersonEmails for new opportunities
      if (!isEditing && !opportunity.salesPersonEmails.includes(currentUser.email)) {
        opportunity.salesPersonEmails = [currentUser.email];
      }

      // Ensure all salespersons exist in Salespersons table
      const existingSalespersons = salesPersons.map(sp => sp.Email || sp.email);
      for (const email of opportunity.salesPersonEmails) {
        if (!existingSalespersons.includes(email)) {
          try {
            // Try to get user info from centralized data first
            let userInfo = userDisplayData[email];
            if (!userInfo) {
              // Only make API call if data not available in cache
              userInfo = await userService.getUserByEmail(email);
            }
            await salesPersonService.createSalesperson(
              email,
              userInfo?.name || email.split('@')[0],
              userInfo?.name ? userInfo.name.split(' ').map(n => n[0]).join('') : email[0].toUpperCase()
            );
          } catch (error) {
            // Continue anyway since the user might already exist
          }
        }
      }

      // Save the opportunity
      const savedOpportunity = await opportunityService.saveOpportunity(opportunity, currentUser.email);

      // Send email notification for new opportunities only
      if (!isEditing) {
        try {
          console.log('Sending opportunity notification email...');
          console.log('Test opportunity checkbox:', isTestOpportunity);
          
          // Ensure the saved opportunity has the created by information
          const opportunityWithCreator = {
            ...savedOpportunity,
            createdByEmail: currentUser.email,
            createdBy: currentUser.name || currentUser.email.split('@')[0] || 'Unknown User'
          };
          
          const emailResult = await emailService.sendOpportunityNotification(
            opportunityWithCreator, 
            isTestOpportunity
          );
          
          console.log('Email notification result:', emailResult);
          
          if (emailResult.success) {
            setSuccess(`Opportunity saved successfully. ${emailResult.message}`);
          } else {
            console.warn('Email notification failed:', emailResult.message);
            setSuccess('Opportunity saved successfully (email notification failed)');
          }
        } catch (error) {
          console.error('Error sending opportunity notification email:', error);
          setSuccess('Opportunity saved successfully (email notification failed)');
        }
      } else {
        setSuccess('Opportunity saved successfully');
      }
      
      // Clear validation errors on successful save
      setValidationErrors({});

      // Navigate back to opportunities list if this was a new opportunity
      if (!isEditing) {
        navigate('/opportunities');
      }
    } catch (err) {
      console.error('Error saving opportunity:', err);
      setError('Failed to save opportunity: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get contact name by ID
  const getContactName = (contactId) => {
    if (!contactId) return 'Unknown Contact';
    
    // First check in opportunity.contacts (from API) - this should be the primary source
    if (opportunity.contacts && Array.isArray(opportunity.contacts)) {
      const opportunityContact = opportunity.contacts.find(c => {
        const cId = (c.contactId || c.ContactId || '').toLowerCase();
        const lookupId = contactId.toLowerCase();
        return cId === lookupId;
      });
      
      if (opportunityContact) {
        const firstName = opportunityContact.firstName || opportunityContact.FirstName || '';
        const lastName = opportunityContact.lastName || opportunityContact.LastName || '';
        const name = `${firstName} ${lastName}`.trim() || 'Unknown Contact';
        return name;
      }
    }
    
    // Fallback to contacts state (from company or unassigned contacts)
    const contact = contacts.find(c => {
      const cId = (c.contactId || c.ContactId || '').toLowerCase();
      const lookupId = contactId.toLowerCase();
      return cId === lookupId;
    });
    
    if (!contact) {
      return 'Unknown Contact';
    }
    
    const firstName = contact.firstName || contact.FirstName || '';
    const lastName = contact.lastName || contact.LastName || '';
    const name = `${firstName} ${lastName}`.trim() || 'Unknown Contact';
    return name;
  };

  // Component to show just the user name (without avatar)
  const UserName = ({ email }) => {
    if (!email) {
      return 'Unknown User';
    }

    // Use centralized user display data instead of making API calls
    const userData = userDisplayData[email];
    
    if (!userData) {
      // Fallback to formatted email prefix if data not available
      return email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    // Return the name if available, otherwise format the email prefix nicely
    return userData.name || email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get company for map
  const getCompanyForMap = () => {
    if (!opportunity.companyId) return null;
    
    // Case-insensitive comparison
    const company = companies.find(c => 
      c.companyId && opportunity.companyId && 
      c.companyId.toLowerCase() === opportunity.companyId.toLowerCase()
    );
    
    if (!company) return null;

    // Return company with camelCase properties
    return {
      ...company,
      companyId: company.companyId,
      companyName: company.companyName,
      street1: company.street1 || company.Street1,
      street2: company.street2 || company.Street2,
      city: company.city || company.City,
      province: company.province || company.Province,
      country: company.country || company.Country,
      postalCode: company.postalCode || company.PostalCode
    };
  };

  // Get contacts for map (in order of contactIds)
  const getContactsForMap = () => {
    if (!opportunity.contactIds || opportunity.contactIds.length === 0) return [];
    
    // Get contacts in the same order as contactIds
    return opportunity.contactIds
      .map(contactId => {
        // Case-insensitive comparison and handle both contactId and ContactId
        const contact = contacts.find(c => 
          (c.contactId || c.ContactId || '').toLowerCase() === contactId.toLowerCase()
        );
        if (!contact) return null;

        // Return contact with both PascalCase and camelCase properties for compatibility
        return {
          ...contact,
          contactId: contact.contactId || contact.ContactId,
          firstName: contact.firstName || contact.FirstName,
          lastName: contact.lastName || contact.LastName,
          street1: contact.street1 || contact.Street1,
          street2: contact.street2 || contact.Street2,
          city: contact.city || contact.City,
          province: contact.province || contact.Province,
          country: contact.country || contact.Country,
          postalCode: contact.postalCode || contact.PostalCode
        };
      })
      .filter(contact => contact); // Remove any undefined contacts
  };

  // Format currency for display (with $ and commas)
  const formatCurrencyForDisplay = (value) => {
    if (value === '' || value === null || value === undefined || isNaN(value)) return '';
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  // Parse currency input (remove $ and commas, return number)
  const parseCurrencyInput = (value) => {
    if (!value || value === '') return '';
    // Remove $ and commas, keep only numbers and decimal point
    const cleanValue = value.toString().replace(/[$,]/g, '');
    return cleanValue;
  };

  // Handle currency field changes
  const handleCurrencyChange = (field, value) => {
    const numericValue = parseCurrencyInput(value);
    handleChange(field, numericValue);
  };

  // Render loading state while waiting for opportunity initialization
  if (!opportunity) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/opportunities')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {isEditing ? 'Edit Opportunity' : 'New Opportunity'}
            </Typography>
            {/* Test Opportunity Checkbox - Only show for admin users during creation */}
            {!isEditing && isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTestOpportunity}
                    onChange={(e) => setIsTestOpportunity(e.target.checked)}
                    color="primary"
                  />
                }
                label="Test Opportunity"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
          
          {/* Refresh Button - Only show for existing opportunities */}
          {isEditing && (
            <IconButton
              onClick={() => loadData(true)}
              disabled={loading}
              title="Refresh data"
              sx={{ color: 'primary.main' }}
            >
              <RefreshIcon />
            </IconButton>
          )}
        </Box>

        {/* Snackbars */}
        <Snackbar
          open={!!error}
          autoHideDuration={error && error.includes('Please fix the following errors') ? 8000 : 6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 2 }}
        >
          <Alert 
            severity="error" 
            onClose={() => setError('')}
            variant="filled"
            sx={{ 
              width: '100%',
              whiteSpace: 'pre-line',
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                lineHeight: 1.4
              }
            }}
          >
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: 2 }}
        >
          <Alert 
            severity="success" 
            onClose={() => setSuccess('')}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {success}
          </Alert>
        </Snackbar>

        <Grid container spacing={3}>
          {/* Main Form */}
          <Grid item xs={12} md={8}>
            {/* Section 1: Opportunity Detail */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1 }} />
                Opportunity Detail
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Opportunity Name *"
                                value={opportunity.opportunityName || ''}
                    onChange={(e) => handleChange('opportunityName', e.target.value)}
                    required
                    error={!!validationErrors.opportunityName}
                    helperText={validationErrors.opportunityName}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Deal Stage</InputLabel>
                    <Select
                                    value={opportunity.dealStage || ''}
                      onChange={(e) => handleStageChange(e.target.value)}
                      label="Deal Stage"
                    >
                      {Object.values(DEAL_STAGES).map(stage => (
                        <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={companies
                      .filter(c => c && c.companyId && c.companyName)
                      .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))}
                    getOptionLabel={(option) => option.companyName || ''}
                    isOptionEqualToValue={(option, value) => 
                      option.companyId === value.companyId
                    }
                    value={companies.find(c => 
                      c.companyId && opportunity.companyId && 
                      c.companyId.toLowerCase() === opportunity.companyId.toLowerCase()
                    ) || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleChange('companyId', newValue.companyId.toLowerCase());
                        handleChange('companyName', newValue.companyName);
                        // Remove all contacts when company changes
                        handleChange('contactIds', []);
                        setContacts([]);
                        // Load contacts for the selected company
                        loadContacts(newValue.companyId);
                      } else {
                        handleChange('companyId', '');
                        handleChange('companyName', '');
                        // Remove all contacts when company is removed
                        handleChange('contactIds', []);
                        setContacts([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Company" sx={{ minWidth: 300 }} />
                    )}
                  />
                  {/* Show company link if selected */}
                  {opportunity.companyId && (
                    <Typography 
                      variant="body2" 
                      color="primary" 
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        mt: 1
                      }}
                      onClick={() => window.open(`/company/${opportunity.companyId}`, '_blank')}
                    >
                      <BusinessIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      View Company Details
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth required error={!!validationErrors.lineOfBusinessId}>
                    <InputLabel>Line of Business *</InputLabel>
                    <Select
                                    value={opportunity.lineOfBusinessId || ''}
                                    onChange={(e) => {
                                        const selectedLOB = lobOptions.find(lob => lob.Id === e.target.value);
                                        handleChange('lineOfBusinessId', e.target.value);
                                        handleChange('lineOfBusinessName', selectedLOB ? selectedLOB.Name : '');
                                    }}
                      label="Line of Business *"
                    >
                      {(() => {
                        // Sort LOBs alphabetically, but put 'Other' last
                        const sorted = [...lobOptions].sort((a, b) => {
                          if ((a.Name || '').toLowerCase() === 'other') return 1;
                          if ((b.Name || '').toLowerCase() === 'other') return -1;
                          return (a.Name || '').localeCompare(b.Name || '');
                        });
                        return sorted.map(lob => (
                                        <MenuItem key={lob.Id} value={lob.Id}>{lob.Name}</MenuItem>
                        ));
                      })()}
                    </Select>
                    {validationErrors.lineOfBusinessId && (
                      <FormHelperText error>{validationErrors.lineOfBusinessId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={opportunity.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe the opportunity, key details, requirements, etc."
                  />
                </Grid>
              </Grid>
            </Paper>

                {/* Section 2: Contacts */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <ContactsIcon sx={{ mr: 1 }} />
                        Contacts
                    </Typography>
                    
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Contact List */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '300px', display: 'flex', flexDirection: 'column' }}>
                  <ContactSelector 
                    companyId={opportunity.companyId || null}
                    opportunityId={opportunity.opportunityId || null}
                    onContactAdded={handleAddContact}
                  />
                  
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <List dense>
                      {(opportunity.contactIds || []).map((contactId, index) => (
                        <ListItem key={contactId}>
                          <ListItemText 
                            primary={
                              <Typography 
                                variant="body2" 
                                color="primary" 
                                sx={{ 
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                                onClick={() => window.open(`/contact/${contactId}`, '_blank')}
                              >
                                {getContactName(contactId)}
                              </Typography>
                            } 
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveContact(index, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUpIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveContact(index, 'down')}
                              disabled={index === opportunity.contactIds.length - 1}
                            >
                              <ArrowDownIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveContact(contactId)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>

              {/* Sales Person List */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '300px', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Sales Persons
                  </Typography>
                  
                  <Autocomplete
                    options={salesPersons.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''))}
                    getOptionLabel={(option) => option.Name || ''}
                    isOptionEqualToValue={(option, value) => 
                      (option.Email || option.email) === (value.Email || value.email)
                    }
                    onChange={(event, newValue) => {
                      if (newValue) handleAddSalesPerson(newValue);
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                        <UserDisplay 
                          email={option.Email || option.email}
                          userData={userDisplayData[option.Email || option.email]}
                          size="small" 
                          showName={false}
                        />
                        <Box sx={{ ml: 2 }}>
                          {option.Name || option.Email || option.email}
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Add Sales Person" size="small" sx={{ minWidth: 300 }} />
                    )}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <List dense>
                      {(() => {
                        const emails = opportunity?.salesPersonEmails || [];
                        return emails.map((email, index) => (
                          <ListItem key={email}>
                            <ListItemAvatar>
                              <UserDisplay 
                                email={email} 
                                userData={userDisplayData[email]}
                                size="small" 
                                showName={false} 
                              />
                            </ListItemAvatar>
                            <ListItemText 
                              primary={<UserName email={email} />}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveSalesPerson(index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveSalesPerson(index, 'down')}
                                disabled={index === emails.length - 1}
                              >
                                <ArrowDownIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSalesPerson(email)}
                                disabled={removingSalesperson === email}
                              >
                                {removingSalesperson === email ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ));
                      })()}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
                </Paper>

                {/* Section 3: Deal Detail */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Deal Detail
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Value"
                    type="text"
                    value={formatCurrencyForDisplay(opportunity.estimatedValue || 0)}
                    onChange={(e) => handleCurrencyChange('estimatedValue', e.target.value)}
                    onBlur={(e) => handleEstimatedValueBlur(parseCurrencyInput(e.target.value))}
                    onFocus={() => {
                      estFocusRef.current = opportunity.estimatedValue;
                    }}
                    placeholder="$0"
                    error={!!validationErrors.estimatedValue}
                    helperText={validationErrors.estimatedValue}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Close Date"
                    type="date"
                    value={opportunity.estimatedCloseDate ? opportunity.estimatedCloseDate.split('T')[0] : ''}
                    onChange={(e) => {
                      const newDate = convertToUTCPreservingET(e.target.value);
                      handleChange('estimatedCloseDate', newDate);
                    }}
                    onBlur={(e) => {
                      const newDate = convertToUTCPreservingET(e.target.value);
                      handleEstimatedCloseDateBlur(newDate);
                    }}
                    onFocus={() => {
                      estCloseDateRef.current = opportunity.estimatedCloseDate;
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!validationErrors.estimatedCloseDate}
                    helperText={validationErrors.estimatedCloseDate}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Probability (%)"
                    type="number"
                    value={opportunity.probability || ''}
                    onChange={(e) => handleChange('probability', e.target.value)}
                    onBlur={handleProbabilityBlur}
                    onFocus={() => {
                      probFocusRef.current = opportunity.probability;
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    error={!!validationErrors.probability}
                    helperText={validationErrors.probability}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Next Step"
                    multiline
                    rows={3}
                    value={opportunity.nextStep || ''}
                    onChange={(e) => handleChange('nextStep', e.target.value)}
                    error={!!validationErrors.nextStep}
                    helperText={validationErrors.nextStep}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Competitor Analysis"
                    multiline
                    rows={3}
                    value={opportunity.competitorAnalysis || ''}
                    onChange={(e) => handleChange('competitorAnalysis', e.target.value)}
                    error={!!validationErrors.competitorAnalysis}
                    helperText={validationErrors.competitorAnalysis}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Section 4: Deal Closure - Only show for closed deals */}
            {isEditing && CLOSED_STAGES.includes(opportunity.dealStage) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Deal Closure
                </Typography>
                
                <Grid container spacing={2}>
                  {opportunity.dealStage === DEAL_STAGES.CANCELLED ? (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cancelled Reason"
                        multiline
                        rows={3}
                        value={opportunity.cancelledReason || ''}
                        onChange={(e) => handleChange('cancelledReason', e.target.value)}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        error={!!validationErrors.cancelledReason}
                        helperText={validationErrors.cancelledReason}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Closed Value"
                        type="text"
                        value={formatCurrencyForDisplay(opportunity.closedValue || 0)}
                        onChange={(e) => handleCurrencyChange('closedValue', e.target.value)}
                        placeholder="$0"
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        error={!!validationErrors.closedValue}
                        helperText={validationErrors.closedValue}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Closed Date"
                      type="date"
                      value={opportunity.closedDate ? opportunity.closedDate.split('T')[0] : ''}
                      onChange={(e) => handleChange('closedDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      error={!!validationErrors.closedDate}
                      helperText={validationErrors.closedDate}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

                {/* Section 5: Comments - Only show for existing opportunities */}
                {isEditing && (
            <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <CommentIcon sx={{ mr: 1 }} />
                Comments
              </Typography>
              
                        {/* Add Comment */}
                        <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                                rows={3}
                                label="Add a comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                                disabled={savingComment}
                            />
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || savingComment}
                >
                                    {savingComment ? <CircularProgress size={24} /> : 'Add Comment'}
                </Button>
                            </Box>
              </Box>
              
                        {/* Comments List */}
                        {opportunity.comments && opportunity.comments.length > 0 ? (
                            <List>
                                {opportunity.comments.map((comment) => (
                                    <ListItem
                                        key={comment.id}
                                        alignItems="flex-start"
                                        sx={{
                                            bgcolor: editingCommentId === comment.id ? 'action.hover' : 'transparent',
                                            borderRadius: 1,
                                            mb: 2
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '150px', mr: 2 }}>
                                                <UserDisplay 
                                                  email={comment.createdByEmail} 
                                                  userData={userDisplayData[comment.createdByEmail]}
                                                  size="small" 
                                                  showName={true} 
                                                />
                                            </Box>
                                            
                                            <Box sx={{ flex: 1 }}>
                                                {editingCommentId === comment.id ? (
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        value={editingCommentText}
                                                        onChange={(e) => setEditingCommentText(e.target.value)}
                                                        disabled={savingComment}
                                                        sx={{ mb: 1 }}
                                                    />
                                                ) : (
                                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                                        {comment.text}
                          </Typography>
                                                )}
                                                
                                                <Typography variant="caption" color="text.secondary">
                            {new Date(comment.createdDate).toLocaleString()}
                                                    {comment.modifiedDate && ' (edited)'}
                          </Typography>
                                            </Box>

                                            <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                              {editingCommentId === comment.id ? (
                                <>
                                  <IconButton
                                    onClick={handleSaveComment}
                                                            disabled={savingComment}
                                                            color="primary"
                                                            size="small"
                                  >
                                                            <SaveIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={handleCancelEditComment}
                                                            disabled={savingComment}
                                                            size="small"
                                  >
                                                            <CancelIcon />
                                  </IconButton>
                                </>
                              ) : (
                                                    comment.createdByEmail === currentUser.email && (
                                <>
                                  <IconButton
                                    onClick={() => handleEditComment(comment)}
                                                                disabled={savingComment}
                                                                size="small"
                                  >
                                                                <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleDeleteComment(comment.id)}
                                                                disabled={savingComment}
                                                                color="error"
                                                                size="small"
                                  >
                                                                <DeleteIcon />
                                  </IconButton>
                                </>
                                                    )
                              )}
                            </Box>
                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography color="text.secondary" align="center">
                                No comments yet
                          </Typography>
                        )}
            </Paper>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    {isEditing && isAdmin && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDeleteOpportunity}
                            disabled={saving}
                            startIcon={<DeleteIcon />}
                        >
                            Delete
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/opportunities')}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
          </Grid>

            {/* Side Panel */}
          <Grid item xs={12} md={4}>
            {/* Map Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                <OpportunityMapSection
                  opportunity={opportunity}
                  company={getCompanyForMap()}
                  contacts={getContactsForMap()}
                />
              </Box>
            </Paper>

            {/* Moments Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TimelineIcon sx={{ mr: 1 }} />
                Moments
              </Typography>
              
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {opportunity.moments
                  .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
                  .map(moment => (
                    <Box key={moment.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {moment.message}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <UserDisplay 
                                          email={moment.createdByEmail} 
                                          userData={userDisplayData[moment.createdByEmail]}
                                          size="small" 
                                          showName={false} 
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {new Date(moment.createdDate).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Paper>

            {/* Created Info */}
            {isEditing && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Created Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Created By:
                  </Typography>
                  <UserDisplay 
                    email={opportunity.createdByEmail} 
                    userData={userDisplayData[opportunity.createdByEmail]}
                    size="small" 
                    showName={true} 
                  />
                </Box>
                <Typography variant="body2">
                  <strong>Created Date:</strong> {
                    opportunity.createdDate ? 
                    new Date(opportunity.createdDate).toLocaleString() : 
                    'N/A'
                  }
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Close Dialog */}
        <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
          <DialogTitle>Close Opportunity</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Closed Stage</InputLabel>
                  <Select
                    value={closeDialogData.stage}
                    onChange={(e) => {
                      const selectedStage = e.target.value;
                      let newValue = closeDialogData.value;
                      
                      // Auto-populate value based on selected stage
                      if (selectedStage === DEAL_STAGES.CLOSED_WON) {
                        // Use current estimated value for won deals
                        newValue = opportunity.estimatedValue || 0;
                      } else if (selectedStage === DEAL_STAGES.CLOSED_LOST) {
                        // Use $0 for lost deals
                        newValue = 0;
                      }
                      setCloseDialogData(prev => ({ 
                        ...prev, 
                        stage: selectedStage,
                        value: newValue
                      }));
                    }}
                    label="Closed Stage"
                  >
                    {CLOSED_STAGES.map(stage => (
                      <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {closeDialogData.stage === DEAL_STAGES.CANCELLED ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancelled Reason"
                    multiline
                    rows={3}
                    value={closeDialogData.reason}
                    onChange={(e) => setCloseDialogData(prev => ({ ...prev, reason: e.target.value }))}
                    error={!!validationErrors.cancelledReason}
                    helperText={validationErrors.cancelledReason}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Closed Value"
                    type="text"
                    value={formatCurrencyForDisplay(closeDialogData.value)}
                    onChange={(e) => setCloseDialogData(prev => ({ ...prev, value: parseCurrencyInput(e.target.value) }))}
                    placeholder="$0"
                    error={!!validationErrors.closedValue}
                    helperText={validationErrors.closedValue}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Closed Date"
                  type="date"
                  value={closeDialogData.date ? closeDialogData.date.toISOString().split('T')[0] : ''}
                  onChange={(e) => setCloseDialogData(prev => ({ ...prev, date: e.target.value ? new Date(e.target.value) : new Date() }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!validationErrors.closedDate}
                  helperText={validationErrors.closedDate}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCloseDialogSave} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default Opportunity;